import type { SlideProgress, UnitProgress } from '../types';

const STORAGE_KEY = 'spoken-english:progress:v1';
const USER_KEY = 'spoken-english:user-id';

export class ProgressStore extends EventTarget {
  private state: Record<string, UnitProgress> = loadState();
  readonly userId = getOrCreateUserId();

  getUnit(unitId: string): UnitProgress {
    return this.state[unitId] ?? { unitId, slides: {} };
  }

  getSlide(unitId: string, slideId: string): SlideProgress | undefined {
    return this.getUnit(unitId).slides[slideId];
  }

  async markVisited(unitId: string, slideId: string): Promise<void> {
    const unit = this.ensureUnit(unitId);
    const previous = unit.slides[slideId];
    unit.slides[slideId] = {
      slideId,
      visited: true,
      completed: previous?.completed ?? false,
      ...(previous?.bestRecording ? { bestRecording: previous.bestRecording } : {}),
      lastVisitedAt: Date.now(),
    };
    if (!unit.startedAt) unit.startedAt = Date.now();
    this.persist();
  }

  async markCompleted(unitId: string, slideId: string): Promise<void> {
    const unit = this.ensureUnit(unitId);
    const previous = unit.slides[slideId];
    unit.slides[slideId] = {
      slideId,
      visited: true,
      completed: true,
      ...(previous?.bestRecording ? { bestRecording: previous.bestRecording } : {}),
      lastVisitedAt: Date.now(),
    };
    this.persist();
  }

  progressRatio(unitId: string, totalSlides: number): { completed: number; total: number } {
    const unit = this.getUnit(unitId);
    const completed = Object.values(unit.slides).filter((s) => s.completed || s.visited).length;
    return { completed, total: totalSlides };
  }

  private ensureUnit(unitId: string): UnitProgress {
    const unit = this.state[unitId] ?? { unitId, slides: {}, startedAt: Date.now() };
    this.state[unitId] = unit;
    return unit;
  }

  private persist(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    this.dispatchEvent(new Event('change'));
  }
}

export const progressStore = new ProgressStore();

function loadState(): Record<string, UnitProgress> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, UnitProgress>) : {};
  } catch {
    return {};
  }
}

function getOrCreateUserId(): string {
  const existing = localStorage.getItem(USER_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(USER_KEY, id);
  return id;
}
